import {useRef, Fragment, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {AlertSizes, AlertTypes} from '@guestapp/ui';
import {COUNTRY_CODES} from 'utils/constants';
import {useGuest, useGuestGroup, useIVComputedDetails} from 'hooks';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useSummary} from 'context/summary';
import {useGetGuestId} from '../../../../hooks/useGetGuestId';
import {useReservation} from 'context/reservation';
import {useKioskoStore} from 'context/kiosko/useKioskoStore';
import {ANALYTIC_EVENTS, Analytics} from '../../../../analytics';
import ChekinProSettings from '../../../../ChekinProSettings';
import {SDK_MODE} from '../../../../utils/embedded';
import VerticalLineIcon from '../../../../assets/icons/vertical-line.svg?react';
import Button from 'components/common/Button';
import {SkipIVModal, SkipIVModalRef} from 'components/common/SkipIVModal';
import {MainLoader} from 'components/common/loaders';
import {ModalTwoButtonsWrapper} from '../../../../styled/common';
import {Wrapper, Text, InfoBox, StyledAlertBox, VerificationSteps} from './styled';
import {VerificationStepItem} from './VerificationStepItem';
import {getVerificationSteps} from './getVerificationSteps';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';

function StartView() {
  const {t} = useTranslation();
  const id = useGetGuestId();
  const {isLoading} = useGuest({id});

  const {biomatch, isDataLoaded} = useIVComputedDetails();

  const skipIVModalRef = useRef<SkipIVModalRef>(null);
  const openSkipModal = () => skipIVModalRef.current?.open();
  const {countryCode} = useSummary();
  const {housing} = useReservation();
  const {
    isEmailEnabled,
    isPhoneEnabled,
    isLoading: isSettingsLoading,
    isNeedVerification,
  } = useVerificationSettings(housing?.id);

  const {guestGroup} = useGuestGroup();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const isExpectedLeaderGuest = !guestGroup?.added_number_of_guests;
  const isAgeInformationVisible =
    isKiosko ||
    (countryCode && countryCode !== COUNTRY_CODES.uae && !isExpectedLeaderGuest);
  const {startVerification} = useRemoteIVFlow(isNeedVerification);

  const getDescriptionItems = useMemo(() => {
    return getVerificationSteps({
      biomatch,
      isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
      isEmailEnabled,
      isPhoneEnabled,
      isNeedContactsVerification: isNeedVerification,
    });
  }, [
    biomatch,
    housing?.is_liveness_detection_enabled,
    isEmailEnabled,
    isPhoneEnabled,
    isNeedVerification,
  ]);

  return (
    <Wrapper className="iv-start-view">
      <Text className="iv-start-view__text">{t('verify_you_identity_to_continue')}</Text>
      <InfoBox className="iv-start-view__info-box">
        {isLoading || isSettingsLoading || !isDataLoaded ? (
          <MainLoader visible width={60} />
        ) : (
          <VerificationSteps>
            {getDescriptionItems.map((step, index, arr) => (
              <Fragment key={`step-${index}`}>
                <VerificationStepItem icon={step.icon} text={t(step.text)} />

                {index < arr.length - 1 && (
                  <VerificationStepItem icon={<VerticalLineIcon />} />
                )}
              </Fragment>
            ))}
          </VerificationSteps>
        )}
      </InfoBox>

      {isAgeInformationVisible && (
        <StyledAlertBox
          text={t('minors_under_18_age')}
          type={AlertTypes.INFO}
          size={AlertSizes.S}
        />
      )}

      <ModalTwoButtonsWrapper className="iv-start-view__buttons">
        <Button
          label={t('start_verification')}
          onClick={() => {
            void startVerification();
            Analytics.track(ANALYTIC_EVENTS.ivStarted, {
              type: biomatch ? 'Biomatch' : 'OCR',
            });
          }}
          disabled={isLoading}
        />
        {ChekinProSettings?.mode !== SDK_MODE.onlyIV && (
          <Button
            label={t('skip_process')}
            onClick={openSkipModal}
            disabled={isLoading}
            link
          />
        )}
      </ModalTwoButtonsWrapper>
      <SkipIVModal ref={skipIVModalRef} />
    </Wrapper>
  );
}

export {StartView};
